import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Phone } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { StatCounter } from "@/components/StatCounter";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { EXPERIENCE, TRAININGS, STATS } from "@/lib/data";
import { assets, CONTACT } from "@/lib/assets";

const Timeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 65%", "end 80%"],
  });
  const lineScale = useSpring(scrollYProgress, { stiffness: 90, damping: 30, restDelta: 0.001 });

  return (
    <section data-testid="experience-section" className="bg-white py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-brand">Career</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-heading font-bold text-navy tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            Work Experience
          </h2>
        </Reveal>

        <div ref={ref} className="relative mt-16 pl-10 sm:pl-16">
          {/* rail */}
          <div className="absolute left-3 sm:left-6 top-2 bottom-2 w-px bg-slate-200" />
          <motion.div
            style={{ scaleY: lineScale }}
            className="absolute left-3 sm:left-6 top-2 bottom-2 w-px bg-emerald-brand origin-top"
          />

          <div className="space-y-14">
            {EXPERIENCE.map((exp) => (
              <Reveal key={exp.company} y={40}>
                <div data-testid={`experience-${exp.company.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="relative">
                  <span className="absolute -left-[38px] sm:-left-[46px] top-1.5 h-4 w-4 rounded-full bg-white border-2 border-emerald-brand ring-4 ring-emerald-brand/10" />
                  <div className="flex flex-wrap items-center gap-4">
                    <img src={exp.logo} alt={exp.company} className="h-14 w-14 object-contain shrink-0" />
                    <div>
                      <span className="text-sm font-bold uppercase tracking-widest text-emerald-brand">{exp.range}</span>
                      <h3 className="font-heading font-semibold text-2xl text-navy leading-tight">{exp.company}</h3>
                    </div>
                  </div>
                  <p className="mt-2 font-heading font-medium text-lg text-slate-700">{exp.role}</p>
                  <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">{exp.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Trainings = () => (
  <section data-testid="trainings-section" className="bg-offwhite py-24 sm:py-32">
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <div className="flex items-start gap-5 mb-12">
        <img src={assets.trainings} alt="" className="h-16 w-16 object-contain shrink-0 hidden sm:block" />
        <div>
          <Reveal><p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-brand">Continuous Learning</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 font-heading font-bold text-navy tracking-tight text-3xl sm:text-4xl lg:text-5xl">
              Trainings, Courses, Seminars & Workshops
            </h2>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.15}>
        <Accordion type="single" collapsible className="rounded-2xl bg-white border border-slate-200/80 overflow-hidden divide-y divide-slate-100">
          {TRAININGS.map((t, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`training-item-${i}`} className="border-b-0 px-6 sm:px-8">
              <AccordionTrigger className="py-6 hover:no-underline group">
                <div className="flex items-center gap-5 text-left">
                  <span className="font-heading font-bold text-emerald-brand text-sm tracking-wider min-w-[92px]">{t.year}</span>
                  <span className="font-heading font-semibold text-navy text-lg sm:text-xl">
                    {t.title}
                    {t.org && <span className="text-slate-400 font-normal text-base"> — {t.org}</span>}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-7">
                {t.items.length > 0 ? (
                  <ul className="pl-[92px] grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                    {t.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-slate-600 leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-brand shrink-0" />
                        {it}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="pl-[92px] text-slate-500">Completed programme.</p>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const Stats = () => (
  <section data-testid="stats-section" className="relative bg-navy-900 grain py-24 sm:py-28 overflow-hidden">
    <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-emerald-brand/10 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-8">
        {STATS.map((s, i) => (
          <StatCounter key={s.label} value={s.value} label={s.label} testId={`stat-counter-${i}`} />
        ))}
      </div>
    </div>
  </section>
);

export default function Team() {
  return (
    <>
      <PageHero
        eyebrow="Experience"
        titleLines={["Decades of", "financial mastery"]}
        subtitle="A track record built across Aruba's leading institutions — from the Big Four to the Aruba Ports Authority."
        testId="team-hero"
      />
      <Timeline />
      <Trainings />
      <Stats />

      <section data-testid="team-final-cta" className="bg-white py-24 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="font-heading font-bold text-navy text-4xl sm:text-5xl tracking-tight">
              Put this experience to work
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              href={`tel:${CONTACT.phone}`}
              data-testid="team-page-cta"
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-navy px-9 py-5 text-sm font-bold uppercase tracking-widest text-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-12px_rgba(9,30,54,0.5)]"
            >
              Get in Touch <Phone size={16} strokeWidth={2.5} className="text-emerald-brand" />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
